/**
 * CombatTrackerContainer - Top-level container for the combat tracker.
 *
 * Follows ADR-034 Container/Presenter pattern:
 * - Connects to Zustand store for combat state and session loading status
 * - Owns the loading timeout logic (10-second timer for loading state)
 * - Wraps all combat action callbacks in useStableCallback
 * - Delegates rendering to CombatTracker presenter
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React, { useState, useEffect, useRef } from 'react';
import { useActions } from '../../hooks/useStore';
import { useSessionData, useIsSessionLoading } from '../../hooks/useSessionData';
import { useStableCallback } from '../../hooks/hookUtils';
import CombatTracker from './CombatTracker';
import { logger } from '../../services/logger.client';
import type { CombatState } from '../../state/combatActions';

/**
 * CombatTrackerContainer manages:
 * 1. Reading combat state from the store
 * 2. Loading state detection and timeout handling
 * 3. Wrapping store actions as stable callbacks for the presenter
 *
 * The presenter (CombatTracker) receives all state and callbacks as props
 * and has ZERO store access.
 */
const CombatTrackerContainer: React.FC = () => {
  const actions = useActions();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Using useStableCallback provides stable references that always call the latest
  // version of actions. This is more efficient than useCallback with [actions] deps
  // because the reference never changes, preventing unnecessary child re-renders.
  const onSetup = useStableCallback(() => actions.setupCombat());
  const onRollAll = useStableCallback(() => actions.rollAllInitiatives());
  const onBegin = useStableCallback(() => actions.beginCombat());
  const onNextTurn = useStableCallback(() => actions.nextTurn());
  const onEnd = useStableCallback(() => actions.endCombat());
  const onLongRest = useStableCallback(() => actions.longRest());

  const combatState = useSessionData(session => session.combat, null);
  const isLoading = useIsSessionLoading();

  // Debug: Log component render state
  logger.debug(
    {
      component: 'CombatTrackerContainer',
      isLoading,
      loadingTimeout,
      hasCombatState: combatState !== null
    },
    'CombatTrackerContainer: Component render'
  );

  // Add timeout for loading state to prevent infinite loading
  useEffect(() => {
    logger.debug(
      { isLoading, loadingTimeout, hasExistingTimer: timerIdRef.current !== null },
      'CombatTrackerContainer: Loading timeout effect triggered'
    );

    if (isLoading) {
      logger.info(
        { creatingTimer: true, timeoutMs: 10000 },
        'CombatTrackerContainer: isLoading=true, creating 10s timeout timer'
      );
      const timer = setTimeout(() => {
        logger.warn(
          { timerFired: true },
          'CombatTrackerContainer: Loading timeout timer fired - setting loadingTimeout=true'
        );
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout
      timerIdRef.current = timer;
      return () => {
        logger.debug(
          { clearingTimer: true },
          'CombatTrackerContainer: Clearing timeout timer (cleanup or isLoading changed)'
        );
        clearTimeout(timer);
        timerIdRef.current = null;
      };
    }
    logger.debug(
      { isLoading: false, resettingTimeout: true },
      'CombatTrackerContainer: isLoading=false, resetting loadingTimeout to false'
    );
    setLoadingTimeout(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadingTimeout is only used in debug logging, not logic. Adding it would cause infinite re-triggers.
  }, [isLoading]);

  // Defensive destructuring with defaults for combat state
  const {
    combatants = [],
    isActive = false,
    setupMode = false,
    round = 0,
    turn = 0
  } = (combatState as CombatState | null) ?? {};

  return (
    <CombatTracker
      combatants={combatants}
      isActive={isActive}
      setupMode={setupMode}
      round={round}
      turn={turn}
      isLoading={isLoading}
      loadingTimeout={loadingTimeout}
      onSetup={onSetup}
      onRollAll={onRollAll}
      onBegin={onBegin}
      onNextTurn={onNextTurn}
      onEnd={onEnd}
      onLongRest={onLongRest}
    />
  );
};

export default CombatTrackerContainer;
